import { useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import type { Json } from "@/integrations/supabase/types";
import { telemetrySampling } from "@/lib/telemetry-config";
import { shouldDedupeSearchEvent, shouldSample } from "@/lib/telemetry-sampling";

/**
 * SEARCH-011: Audit & Telemetry (Internal)
 * 
 * Track search usage for quality, not gamification.
 * 
 * Metrics:
 * - Time to first action
 * - Search → completion rate
 * - Zero-result frequency
 * 
 * Rules:
 * - No user-facing metrics
 * - No ranking feedback loops
 */

type TelemetryEventType = 'search' | 'select' | 'action' | 'zero_results';

interface TelemetryEvent {
  eventType: TelemetryEventType;
  query?: string;
  objectTypes?: string[];
  resultCount?: number;
  selectedObjectType?: string;
  selectedObjectId?: string;
  actionType?: string;
  timeToActionMs?: number;
  metadata?: Json;
}

interface UseSearchTelemetryOptions {
  source?: string;
}

// Generate session ID for correlating events
function getSessionId(): string {
  const canUseStorage = typeof window !== "undefined" && "sessionStorage" in window;
  const generateSessionId = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    return `search-${Math.random().toString(36).slice(2, 10)}`;
  };

  if (!canUseStorage) {
    return generateSessionId();
  }

  try {
    let sessionId = window.sessionStorage.getItem("search_session_id");
    if (!sessionId) {
      sessionId = generateSessionId();
      window.sessionStorage.setItem("search_session_id", sessionId);
    }
    return sessionId;
  } catch (error) {
    console.debug("[telemetry] Unable to access session storage:", error);
    return generateSessionId();
  }
}

export function useSearchTelemetry(options: UseSearchTelemetryOptions = {}) {
  const { organization } = useOrganization();
  const { user } = useAuth();
  const { isPlatformAdmin } = useUserRoles();
  const sessionId = useMemo(() => getSessionId(), []);
  const source = options.source ?? "search";
  const searchSampleRate = telemetrySampling.search?.searchRate ?? 0.2;
  const selectSampleRate = telemetrySampling.search?.selectRate ?? 0.2;
  const dedupeWindowMs = telemetrySampling.search?.dedupeWindowMs ?? 20000;
  
  // Mutation to record telemetry
  const telemetryMutation = useMutation({
    mutationFn: async (event: TelemetryEvent) => {
      if (!user?.id) return;

      const organizationId = organization?.id ?? null;
      const canLogOrgless = isPlatformAdmin;

      if (!organizationId && !canLogOrgless) return;

      const isOrglessPlatformEvent = !organizationId && canLogOrgless;
      const resolvedSource = isOrglessPlatformEvent ? "admin" : source;
      
      const { error } = await supabase
        .from("search_telemetry")
        .insert({
          organization_id: organizationId,
          user_id: user.id,
          session_id: sessionId,
          event_type: event.eventType,
          source: resolvedSource,
          query: event.query ?? null,
          object_types: event.objectTypes ?? null,
          result_count: event.resultCount ?? 0,
          selected_object_type: event.selectedObjectType ?? null,
          selected_object_id: event.selectedObjectId ?? null,
          action_type: event.actionType ?? null,
          time_to_action_ms: event.timeToActionMs ?? null,
          metadata: event.metadata ?? null,
        });
      
      if (error) {
        // Silent fail - telemetry should never block user actions
        console.debug("[telemetry] Error recording event:", error);
      }
    },
  });

  // IMPORTANT (UAT 3.6): depend on the stable `mutate` function, NOT on the
  // `useMutation` result object. That object is recreated on every render, so
  // putting it in the useCallback deps below made trackSearch/trackAction get
  // a new identity each render. Consumers that listed those callbacks in a
  // useEffect dependency array then re-ran the effect on every render, and
  // because mutate() itself re-renders the component, the result was a
  // self-sustaining telemetry POST loop (600+ /rest/v1/search_telemetry
  // requests in one session).
  const { mutate: recordEvent } = telemetryMutation;


  // Track a search event
  const trackSearch = useCallback(
    (query: string, resultCount: number, objectTypes?: string[]) => {
      if (!shouldSample(searchSampleRate)) {
        return;
      }
      if (shouldDedupeSearchEvent(query, dedupeWindowMs)) {
        return;
      }
      recordEvent({
        eventType: resultCount === 0 ? 'zero_results' : 'search',
        query,
        resultCount,
        objectTypes,
      });
    },
    [dedupeWindowMs, searchSampleRate, recordEvent]
  );
  
  // Track result selection
  const trackSelect = useCallback(
    (objectType: string, objectId: string, query?: string, timeToActionMs?: number) => {
      if (!shouldSample(selectSampleRate)) {
        return;
      }
      recordEvent({
        eventType: 'select',
        selectedObjectType: objectType,
        selectedObjectId: objectId,
        query,
        timeToActionMs,
      });
    },
    [selectSampleRate, recordEvent]
  );
  
  // Track action taken
  const trackAction = useCallback(
    (actionType: string, query?: string, objectType?: string, objectId?: string) => {
      recordEvent({
        eventType: 'action',
        actionType,
        query,
        selectedObjectType: objectType,
        selectedObjectId: objectId,
      });
    },
    [recordEvent]
  );
  
  return {
    trackSearch,
    trackSelect,
    trackAction,
  };
}
